import { t } from "./i18n";

// The text rules of the chat search: how text splits into words, what a query
// matches, which part of a message is its searchable text, and how a match is
// marked. Pure functions, except the last helpers, which only ever touch text
// nodes.

// tuwunel indexes a message by splitting its body on everything that is not a
// letter or a digit, lowercased, words up to 50 characters. Queries are cut
// the same way, so what the lists mark is what the server matched.
const SEPARATORS = /[^\p{L}\p{N}]+/u;
const WORDS = /[\p{L}\p{N}]+/gu;
const MAX_WORD_LENGTH = 50;

const MEDIA_TYPES = new Set(["m.image", "m.video", "m.audio", "m.file"]);

// Where a rendered message shows its own words: its text and caption (both go
// through renderFormattedText) and a file's name. Not a reply's quote, a link
// preview or the sender's name.
const MESSAGE_TEXT = ".bryght-message-text-content, .bryght-message-file";
const NOT_MESSAGE_TEXT = ".bryght-message-file-size, .bryght-search-mark";

export function tokenize(text) {
  if (!text) return [];
  return String(text).toLowerCase().split(SEPARATORS)
    .filter((word) => word && word.length <= MAX_WORD_LENGTH);
}

export function queryTokens(query) {
  return [...new Set(tokenize(query))];
}

// WhatsApp's rule: every query word starts some word of the text.
export function matchesAllPrefixes(text, tokens) {
  if (!tokens.length) return false;
  const words = tokenize(text);
  return tokens.every((token) => words.some((word) => word.startsWith(token)));
}

// Where query words start words of the text, as [{ start, end }]. The longest
// matching token wins a word, so "hal hallo" marks all of "Hallo".
export function findMatchRanges(text, tokens) {
  const ranges = [];
  if (!text || !tokens.length) return ranges;

  for (const match of String(text).matchAll(WORDS)) {
    const word = match[0].toLowerCase();
    let length = 0;
    for (const token of tokens) {
      if (token.length > length && word.startsWith(token)) length = token.length;
    }
    if (length) ranges.push({ start: match.index, end: match.index + Math.min(length, match[0].length) });
  }
  return ranges;
}

// For chat names only: lowercase and without accents, so "mul" finds "Müller".
export function normalizeName(text) {
  return String(text || "").normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

// A reply's body carries the quoted message as a fallback. What the reply
// itself says is its formatted body without the <mx-reply> block — the text
// the timeline renders.
export function stripReplyFallback(content) {
  const body = content?.body || "";
  if (content?.format !== "org.matrix.custom.html" || !content.formatted_body?.includes("<mx-reply>")) return body;

  try {
    const doc = new DOMParser().parseFromString(content.formatted_body, "text/html");
    doc.querySelector("mx-reply")?.remove();
    return doc.body.textContent.trim();
  } catch (e) {
    return body.replace(/^> <@[^>]+>.*(\n>.*)*\n\n/, "").trim();
  }
}

// The words a message shows: a media message's caption, else its file name;
// a text message's own text.
export function messageSearchText(content) {
  if (!content) return "";
  if (MEDIA_TYPES.has(content.msgtype)) {
    const hasCaption = content.filename && content.body && content.body !== content.filename;
    return hasCaption ? content.body : (content.filename || content.body || "");
  }
  return stripReplyFallback(content);
}

// An edit (m.replace) stands for the message it edits, with the new content.
// The chat never sends edits; other clients might.
export function resolveEditTarget(event) {
  const relation = event.content?.["m.relates_to"];
  if (relation?.rel_type === "m.replace" && relation.event_id) {
    return { targetId: relation.event_id, content: event.content["m.new_content"] || null, isEdit: true };
  }
  return { targetId: event.event_id, content: event.content || null, isEdit: false };
}

// One line of text around the first match: whitespace collapsed, starting a
// few words before the match so it is visible in a row that ellipsizes.
export function buildSnippet(text, tokens, { before = 30, max = 120 } = {}) {
  const flat = String(text || "").replace(/\s+/g, " ").trim();
  const ranges = findMatchRanges(flat, tokens);

  let start = 0;
  if (ranges.length && ranges[0].start > before) {
    start = ranges[0].start - before;
    // Start at a word, not inside one.
    const space = flat.indexOf(" ", start);
    if (space !== -1 && space < ranges[0].start) start = space + 1;
  }
  const end = Math.min(flat.length, start + max);

  return {
    text: flat.slice(start, end),
    ranges: ranges
      .filter((range) => range.start >= start && range.end <= end)
      .map((range) => ({ start: range.start - start, end: range.end - start })),
    leading: start > 0,
    trailing: end < flat.length
  };
}

// Appends text with its ranges wrapped in <mark>, as text nodes only — the
// text is someone's message and never becomes markup.
export function appendHighlighted(parent, text, ranges) {
  let position = 0;
  for (const { start, end } of ranges) {
    if (start > position) parent.appendChild(document.createTextNode(text.slice(position, start)));
    const mark = document.createElement("mark");
    mark.className = "bryght-search-mark";
    mark.textContent = text.slice(start, end);
    parent.appendChild(mark);
    position = end;
  }
  if (position < text.length) parent.appendChild(document.createTextNode(text.slice(position)));
}

// Marks the query words in a rendered message. Returns how many it marked.
export function highlightInElement(root, tokens) {
  if (!root || !tokens?.length) return 0;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const parent = node.parentElement;
      return parent?.closest(MESSAGE_TEXT) && !parent.closest(NOT_MESSAGE_TEXT)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    }
  });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);

  let count = 0;
  for (const node of nodes) {
    const ranges = findMatchRanges(node.data, tokens);
    if (!ranges.length) continue;
    const fragment = document.createDocumentFragment();
    appendHighlighted(fragment, node.data, ranges);
    node.replaceWith(fragment);
    count += ranges.length;
  }
  return count;
}

export function clearHighlights(root) {
  root?.querySelectorAll("mark.bryght-search-mark").forEach((mark) => {
    const parent = mark.parentNode;
    mark.replaceWith(document.createTextNode(mark.textContent));
    parent.normalize();
  });
}

// Today the time, yesterday "Gestern", this week the weekday, then the date.
export function formatSearchDate(ts, now = new Date()) {
  if (!ts) return "";
  const date = new Date(ts);
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);

  if (days === 0) return date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  if (days === 1) return t("yesterday", "Gestern");
  if (days > 1 && days < 7) return date.toLocaleDateString("de-DE", { weekday: "long" });
  return date.toLocaleDateString("de-DE", date.getFullYear() === now.getFullYear()
    ? { day: "2-digit", month: "2-digit" }
    : { day: "2-digit", month: "2-digit", year: "2-digit" });
}
